/**
 * Helpers puros do módulo de atendimento.
 * Mantidos isolados para facilitar teste unitário e reduzir
 * complexity/max-statements das actions e views.
 */
import { parseBackendDate } from '@modules/atendimento/utils/message-formatter'

// Campos que descrevem a "última mensagem" exibida na lista lateral. Movem
// sempre juntos — preview, horário e a flag de nota interna.
const LAST_MESSAGE_FIELDS = [
  'ultima_mensagem_em',
  'ultima_mensagem_preview',
  'ultima_mensagem_is_note',
]

/**
 * Localiza o índice de uma conversa pelo `id` em uma lista.
 *
 * @param {Array<{id: *}>} list
 * @param {*} id
 * @returns {number} índice, ou -1 se não encontrada
 */
export function findConversaIndex(list, id) {
  if (!Array.isArray(list)) return -1
  for (let i = 0; i < list.length; i++) {
    if (list[i] && list[i].id === id) return i
  }
  return -1
}

/**
 * Mescla a lista atual de conversas com as conversas atualizadas vindas
 * do polling. Para cada conversa atualizada:
 * - Se já existe na lista (mesmo `id`), faz merge raso (campos atualizados
 *   sobrescrevem os anteriores).
 * - Se não existe, é anexada ao final (mesma semântica do código original).
 *
 * Não muta os arrays/objetos de entrada — devolve uma nova lista.
 *
 * @param {Array<Object>} current
 * @param {Array<Object>} updated
 * @returns {Array<Object>} nova lista de conversas
 */
/**
 * Constrói um mapa de IDs existentes a partir de um array de mensagens.
 * Usado para evitar duplicação ao inserir novas mensagens via polling.
 *
 * @param {Array<{id: *}>} mensagens
 * @returns {Object} Mapa { id: true }
 */
export function buildExistingIdsMap(mensagens) {
  const ids = {}
  if (!Array.isArray(mensagens)) return ids
  for (const msg of mensagens) {
    if (msg && msg.id) ids[msg.id] = true
  }
  return ids
}

// True quando o delta `upd` traz uma última mensagem ESTRITAMENTE mais
// antiga que a já presente em `current`. Deltas de polling podem chegar
// fora de ordem ou stale (ex.: a conversa aberta acabou de receber uma
// mensagem nova via patch, e um delta atrasado ainda carrega a anterior).
function isStaleLastMessage(current, upd) {
  const curEm = current && current.ultima_mensagem_em
  const updEm = upd && upd.ultima_mensagem_em
  // Sem horário atual, ou o delta não traz horário → não há regressão a evitar.
  if (!curEm || !updEm) return false
  const cur = parseBackendDate(curEm)
  const next = parseBackendDate(updEm)
  if (!cur || !next) return false
  return next.getTime() < cur.getTime()
}

// Merge raso de uma conversa com guarda monotônica na última mensagem:
// todos os campos do delta são aplicados, EXCETO os de "última mensagem"
// quando o delta é mais antigo — assim a lateral nunca regride para uma
// mensagem anterior, vindo de qualquer fonte de atualização.
function mergeConversaMonotonic(current, upd) {
  const merged = { ...current, ...upd }
  if (isStaleLastMessage(current, upd)) {
    for (const field of LAST_MESSAGE_FIELDS) {
      merged[field] = current[field]
    }
  }
  return merged
}

export function mergeUpdatedConversas(current, updated) {
  const result = Array.isArray(current) ? current.slice() : []
  if (!Array.isArray(updated) || updated.length === 0) return result
  for (const upd of updated) {
    if (!upd || upd.id === undefined) continue
    const idx = findConversaIndex(result, upd.id)
    if (idx >= 0) {
      result[idx] = mergeConversaMonotonic(result[idx], upd)
    } else {
      result.push(upd)
    }
  }
  return result
}

/**
 * Decide se a conversa selecionada deve ser marcada como lida com base
 * no resultado de um poll. Verdadeiro só quando a conversa atualizada
 * existe no payload e tem `mensagens_nao_lidas > 0` — caso em que o user
 * já está vendo as mensagens (a conversa está aberta), então o badge na
 * lista lateral é ruído.
 *
 * @param {Object|null} selected - conversa atualmente selecionada
 * @param {Array<Object>} conversasAtualizadas - payload do pollConversas
 * @returns {boolean}
 */
export function shouldMarkSelectedAsRead(selected, conversasAtualizadas) {
  if (!selected || selected.id === undefined) return false
  if (!Array.isArray(conversasAtualizadas)) return false
  const updated = conversasAtualizadas.find((c) => c && c.id === selected.id)
  if (!updated) return false
  return Number(updated.mensagens_nao_lidas) > 0
}

/**
 * Checa se uma conversa casa com um termo de busca livre.
 *
 * A busca é case-insensitive para nome do contato, nome do cliente
 * e preview da última mensagem. O telefone é comparado como substring
 * direta (números não têm variação de caixa).
 *
 * @param {Object} conversa
 * @param {string} search - termo já normalizado (trimmed, lowercase para texto)
 * @returns {boolean}
 */
export function matchesConversaSearch(conversa, search) {
  if (!conversa || !search) return true
  const contato = conversa.contato || {}
  const nome = (contato.nome || '').toLowerCase()
  const nomeCliente = (contato.nome_cliente || '').toLowerCase()
  const tel = contato.telefone || ''
  const preview = (conversa.ultima_mensagem_preview || '').toLowerCase()
  return (
    nome.includes(search) ||
    nomeCliente.includes(search) ||
    tel.includes(search) ||
    preview.includes(search)
  )
}

// Params de escopo para listarConversas: id_cliente_app ou fallback telefone.
export function buildScopeParams(item = {}) {
  const data = item || {}
  if (data.id_cliente_app) return { id_cliente_app: data.id_cliente_app }
  if (data.telefone) return { telefone: data.telefone }
  return null
}

// True quando o lead/cliente tem algum vínculo de atendimento possível.
export function hasScope(item = {}) {
  return buildScopeParams(item) !== null
}

/**
 * Há alguma conversa com mensagens não lidas? Gate do botão "Resumir não
 * lidas com a IA" — sem nada para resumir, o botão não aparece.
 *
 * @param {Array} conversas lista da store (`mensagens_nao_lidas` por item)
 * @returns {boolean}
 */
export function temNaoLidas(conversas) {
  return (conversas || []).some(
    (conversa) => Number((conversa || {}).mensagens_nao_lidas) > 0
  )
}
